/**
 * The UI languages of Music24: the locale files of the web app (`music24-app/src/i18n/locales`)
 * and of music24-api (`music24-api/src/lang`). `UserModel.music24Locale` holds one of them, or
 * null for "Automatic". This file has no imports, so every project may read it.
 */
export const MUSIC24_LOCALES = ['en', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'sv'] as const;

export type Music24Locale = (typeof MUSIC24_LOCALES)[number];

export function isMusic24Locale(value: unknown): value is Music24Locale {
  return typeof value === 'string' && (MUSIC24_LOCALES as readonly string[]).includes(value);
}

/**
 * The name of each language in that language. The picker shows these names in every UI language,
 * so a user who cannot read the current language still finds their own.
 */
export const MUSIC24_LOCALE_NAMES: Record<Music24Locale, string> = {
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  pt: 'Português',
  sv: 'Svenska',
};

/**
 * The language part of a locale (`pt-BR` → `pt`) when Music24 has it, else null. Use it for a
 * browser, device or licence locale.
 */
export function music24LocaleOf(locale: string | null | undefined): Music24Locale | null {
  if (locale === null || locale === undefined) {
    return null;
  }
  const language = locale.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  return isMusic24Locale(language) ? language : null;
}

/**
 * The language of a message for a user: the language the user picked, else the fallback
 * (the licence language, a device locale), else English.
 */
export function preferredMusic24Locale(
  userLocale: string | null | undefined,
  fallback: string | null | undefined
): Music24Locale {
  return music24LocaleOf(userLocale) ?? music24LocaleOf(fallback) ?? 'en';
}
